import * as React from 'react';
import {CSSProperties, useCallback} from 'react';
import styled from 'styled-components';
import {OnChangeFieldValue} from '../field/FieldValue';
import {Field, Option} from '../types/Field';
import {array} from '../util';
import {
  CheckboxWidget,
  OnChangeFn,
  RenderLabelFn,
} from '../widgets/CheckboxWidget';
import {widgetProps} from '../widgets/common';
import {Actions} from '../types/Action';
import {textColor} from '../styles';
import {ItemDetailsDialog} from '../common/dialogs/ItemDetailsDialog';
import {useApiContext} from '../api';
import {Params} from '../types/Params';

type Props = {
  value: any;
  field: Field;
  onChange?: (v: any) => void;
  actions?: Actions;
  readOnly?: boolean;
  style?: CSSProperties;
};

export function CheckboxField(props: Props) {
  const capacity = widgetProps<number>(props.field, 'capacity', 0);
  const vertical = widgetProps<boolean>(props.field, 'vertical', false);
  const customLabel = useCustomLabel(props);

  const fn = useCallback<OnChangeFn>(
    (v, checked) => {
      onChangeValue(
        props.field.id,
        capacity,
        props.value,
        v,
        checked,
        props.onChange,
      );
    },
    [props.field.id, capacity, props.value, props.onChange],
  );

  if (!props.field.options) {
    return null;
  }

  return (
    <Container style={props.style}>
      <CheckboxWidget
        {...props}
        options={props.field.options}
        onChange={fn}
        vertical={vertical}
        onRenderLabel={customLabel?.onRenderLabel}
      />
      {customLabel?.Component}
    </Container>
  );
}

export function onChangeValue(
  fieldId: string,
  capacity: number,
  value: any,
  newValue: string,
  checked?: boolean,
  onChange?: OnChangeFieldValue,
) {
  // copy to prevent value object from being updated
  let newValues = [...ignoreEmpty(array(value))];
  const index = newValues.indexOf(newValue);

  if (checked && index < 0) {
    newValues.push(newValue);
  } else if (index > -1) {
    newValues.splice(index, 1);
  }

  if (capacity > 0 && newValues.length > capacity) {
    newValues = newValues.slice(newValues.length - capacity);
  }

  if (onChange) {
    onChange({[fieldId]: newValues});
  }
}

function ignoreEmpty(values: string[]): string[] {
  return values.filter((v) => v !== '');
}

type CustomLabel = {
  onRenderLabel?: RenderLabelFn;
  Component?: JSX.Element;
};

type Args = {
  schemaId: string;
  resId: string;
  params?: Params;
};

function useCustomLabel(props: Props): CustomLabel | undefined {
  const onPressLabel = widgetProps<string>(props.field, 'on_press_label', '');
  const ctx = useApiContext();
  const [args, setArgs] = React.useState<Args | null>(null);

  if (onPressLabel === 'open-in-new-tab') {
    return {
      onRenderLabel: (option: Option): JSX.Element | null => {
        return (
          <Link href={option.props?.link?.url} target={'_blank'}>
            {option.label}
          </Link>
        );
      },
    };
  }

  if (onPressLabel === 'open-in-dialog') {
    return {
      onRenderLabel: (option: Option): JSX.Element | null => {
        return (
          <Link
            target={'_blank'}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();

              setArgs(buildArgs(option));
            }}>
            {option.label}
          </Link>
        );
      },
      Component: (
        <ItemDetailsDialog
          ctx={ctx}
          shown={!!args}
          schemaId={args?.schemaId}
          resId={args?.resId}
          params={args?.params}
          onClose={function (): void {
            setArgs(null);
          }}
          actions={props.actions || {}}
        />
      ),
    };
  }
}

function buildArgs(option: Option): Args | null {
  if (!option.props?.link) {
    return null;
  }

  return {
    schemaId: option.props.link.schemaId,
    resId: option.props.link.resId,
    params: option.props.link.params,
  };
}

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Link = styled.a`
  color: ${textColor};
`;
